/**
 * Utilities for checking user permissions in group conversations.
 * Based on role hierarchy: owner (3) > admin (2) > standard (1)
 */

const getParticipantUserId = (participant) =>
  `${participant?.user_id ?? participant?.userId ?? participant?.user?.id ?? participant?.id ?? ''}`;

/**
 * Get the current user's participant object and role in a conversation.
 * @param {object} conversation
 * @param {number} currentUserId
 * @returns {{ participant: object | null, role: 'owner' | 'admin' | 'standard' | null }}
 */
export function getCurrentUserRole(conversation, currentUserId) {
  if (!conversation?.participants || !currentUserId) {
    return { participant: null, role: null };
  }

  const participant = conversation.participants.find(
    (p) => getParticipantUserId(p) === `${currentUserId}` && p.left_at === null,
  );

  return {
    participant: participant || null,
    role: participant?.role || null,
  };
}

/**
 * Check if user can promote someone to admin.
 * Only owners can assign admin role.
 */
export function canPromoteToAdmin(myRole) {
  return myRole === 'owner';
}

/**
 * Check if user can demote someone to standard.
 * Owners and admins can assign standard role.
 */
export function canDemoteToStandard(myRole) {
  return myRole === 'owner' || myRole === 'admin';
}

/**
 * Check if user can transfer ownership.
 * Only the current owner can transfer ownership.
 */
export function canTransferOwnership(myRole) {
  return myRole === 'owner';
}

/**
 * Check if user can add participants.
 * Owners and admins can add participants.
 */
export function canAddParticipants(myRole) {
  return myRole === 'owner' || myRole === 'admin';
}

/**
 * Check if user can remove a specific participant.
 * Owners and admins can remove participants, but not themselves or the owner.
 * @param {string} myRole - Current user's role
 * @param {object} targetParticipant - The participant to potentially remove
 * @param {number} currentUserId - Current user's ID
 */
export function canRemoveParticipant(myRole, targetParticipant, currentUserId) {
  // Can't remove yourself
  if (getParticipantUserId(targetParticipant) === `${currentUserId}`) return false;

  // Can't remove the owner
  if (targetParticipant.role === 'owner') return false;

  // Owner and admin can remove standard users
  if (myRole === 'owner' || myRole === 'admin') {
    return true;
  }

  return false;
}

/**
 * Check if user can change a specific participant's role.
 * @param {string} myRole - Current user's role
 * @param {object} targetParticipant - The participant whose role would be changed
 * @param {string} desiredRole - The role to assign ('admin' or 'standard')
 * @param {number} currentUserId - Current user's ID
 */
export function canChangeParticipantRole(myRole, targetParticipant, desiredRole, currentUserId) {
  // Can't change your own role
  if (getParticipantUserId(targetParticipant) === `${currentUserId}`) return false;

  // Can't change the owner's role (use transfer ownership instead)
  if (targetParticipant.role === 'owner') return false;

  // Promoting to admin requires owner
  if (desiredRole === 'admin' && myRole !== 'owner') return false;

  // Demoting to standard requires admin or owner
  if (desiredRole === 'standard' && !['admin', 'owner'].includes(myRole)) return false;

  return true;
}

/**
 * Get a list of available role actions for a specific participant.
 * @param {string} myRole - Current user's role
 * @param {object} targetParticipant - The participant to get actions for
 * @param {number} currentUserId - Current user's ID
 * @returns {Array<{ label: string, value: string, action: string }>}
 */
export function getAvailableRoleActions(myRole, targetParticipant, currentUserId) {
  const actions = [];

  // Can't act on yourself or the owner
  if (
    getParticipantUserId(targetParticipant) === `${currentUserId}` ||
    targetParticipant.role === 'owner'
  ) {
    return actions;
  }

  const currentRole = targetParticipant.role;

  // Option to promote to admin (if not already admin)
  if (
    currentRole !== 'admin' &&
    canChangeParticipantRole(myRole, targetParticipant, 'admin', currentUserId)
  ) {
    actions.push({
      label: 'Make Admin',
      value: 'admin',
      action: 'promote',
    });
  }

  // Option to demote to standard (if not already standard)
  if (
    currentRole !== 'standard' &&
    canChangeParticipantRole(myRole, targetParticipant, 'standard', currentUserId)
  ) {
    actions.push({
      label: 'Make Standard',
      value: 'standard',
      action: 'demote',
    });
  }

  // Option to transfer ownership (only for owner)
  if (canTransferOwnership(myRole)) {
    actions.push({
      label: 'Transfer Ownership',
      value: 'owner',
      action: 'transfer',
    });
  }

  // Option to remove
  if (canRemoveParticipant(myRole, targetParticipant, currentUserId)) {
    actions.push({
      label: 'Remove from Group',
      value: 'remove',
      action: 'remove',
    });
  }

  return actions;
}

/**
 * Check if user can edit the group chat name.
 * Only admins and owners can change group names.
 * @param {string} myRole - Current user's role
 * @param {object} conversation - The conversation
 */
export function canEditGroupName(myRole, conversation) {
  // Only applies to group conversations
  if (conversation?.type !== 'group') return false;

  // Only admin and owner can edit
  return myRole === 'owner' || myRole === 'admin';
}

/**
 * Get a human-readable label for a role.
 */
export function getRoleLabel(role) {
  const labels = {
    owner: 'Owner',
    admin: 'Admin',
    standard: 'Member',
  };
  return labels[role] || 'Member';
}
